import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getDashboardVulnerabilityDetail, patchDashboardVulnerabilityStatus, postDashboardVulnerabilityNote } from '../../../services/risk.service';
import '../vulnerabilities.css';

const RISK_STATUS_OVERRIDES_KEY = 'xspectre_risk_status_overrides';
const RISK_NOTES_OVERRIDES_KEY = 'xspectre_risk_notes_overrides';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const RISK_DETAIL_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'accepted', label: 'Accept Risk' },
  { value: 'false_positive', label: 'False Positive' },
  { value: 'closed', label: 'Close' },
];

function riskStatusLabelFromValue(statusValue) {
  const status = String(statusValue || '').trim().toLowerCase();
  const match = RISK_DETAIL_STATUS_OPTIONS.find((opt) => opt.value === status);
  if (match) return match.label;
  if (status === 'false_positive') return 'False Positive';
  if (status === 'accepted') return 'Accept Risk';
  if (status === 'closed') return 'Close';
  return 'Open';
}

function RiskDetailStatusSelect({ value, disabled = false, onChange }) {
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const status = String(value || 'open').trim().toLowerCase() || 'open';
  const selectedLabel = riskStatusLabelFromValue(status);
  const isLightTheme = Boolean(
    typeof document !== 'undefined' && document.querySelector('.dashboard-container.theme-light')
  );

  const syncMenuPosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) return;
    const rect = button.getBoundingClientRect();
    const gap = 6;
    const menuWidth = Math.max(rect.width, 168, 148);
    let left = rect.right - menuWidth;
    if (left < 8) left = 8;
    if (left + menuWidth > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - menuWidth - 8);
    }
    const estimatedHeight = RISK_DETAIL_STATUS_OPTIONS.length * 36 + 16;
    const openUp = window.innerHeight - rect.bottom < estimatedHeight + 12;
    setMenuStyle({
      position: 'fixed',
      top: openUp ? undefined : rect.bottom + gap,
      bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
      left,
      width: menuWidth,
      zIndex: 5300,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return undefined;
    }
    syncMenuPosition();
    window.addEventListener('resize', syncMenuPosition);
    window.addEventListener('scroll', syncMenuPosition, true);
    return () => {
      window.removeEventListener('resize', syncMenuPosition);
      window.removeEventListener('scroll', syncMenuPosition, true);
    };
  }, [open, syncMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inRoot && !inMenu) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div
      className={`risk-detail-status-control risk-detail-status-control--header${open ? ' is-open' : ''}`}
      ref={rootRef}
      data-tour="risk-detail-status"
    >
      <button
        ref={buttonRef}
        type="button"
        className={`risks-list-action-select risks-list-action-select--${status}${open ? ' is-open' : ''} risk-detail-status-trigger`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Status: ${selectedLabel}`}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
      >
        <span className="risks-list-action-select-label">{selectedLabel}</span>
        <span className="risks-list-action-select-chevron" aria-hidden="true" />
      </button>
      {open && menuStyle
        ? createPortal(
            <ul
              ref={menuRef}
              className={`risks-list-status-menu${isLightTheme ? ' risks-list-status-menu--light' : ''}`}
              role="listbox"
              aria-label="Select status"
              style={menuStyle}
            >
              {RISK_DETAIL_STATUS_OPTIONS.map((option) => (
                <li key={option.value} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={status === option.value}
                    className={`risks-list-status-menu-option risks-list-status-menu-option--${option.value}${
                      status === option.value ? ' is-selected' : ''
                    }`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => {
                      onChange(option.value);
                      setOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>,
            document.body
          )
        : null}
    </div>
  );
}

function normalizeRiskState(raw) {
  if (String(raw?.accepted_vuln || '').trim().toLowerCase() === 'yes') return 'accepted';
  if (String(raw?.false_positive || '').trim().toLowerCase() === 'yes') return 'false_positive';
  const status = String(raw?.vuln_status || '').trim().toLowerCase();
  if (status === 'closed') return 'closed';
  return 'open';
}

function resolveRiskDetailStatusValue(raw, row) {
  if (raw && typeof raw === 'object' && Object.keys(raw).length > 0) {
    return normalizeRiskState(raw);
  }
  const rowStatus = String(row?.status || 'open').trim().toLowerCase();
  return rowStatus || 'open';
}

function formatRiskDetectedAt(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

function firstNonEmpty(...candidates) {
  for (const candidate of candidates) {
    const text = String(candidate ?? '').trim();
    if (text) return text;
  }
  return '';
}

function looksLikeIpAddress(value) {
  const text = String(value ?? '').trim();
  if (!text) return false;
  // IPv4
  if (/^(?:\d{1,3}\.){3}\d{1,3}$/.test(text)) return true;
  // Loose IPv6 / compressed IPv6
  if (text.includes(':') && /^[0-9a-fA-F:]+$/.test(text)) return true;
  return false;
}

function pickRiskIp(...candidates) {
  for (const candidate of candidates) {
    const text = firstNonEmpty(candidate);
    if (text && looksLikeIpAddress(text)) return text;
  }
  // Last resort: first non-empty value even if it isn't a strict IP parse.
  return firstNonEmpty(...candidates);
}

/** Prefer detail fields, but keep seed values when the API omits or nulls them. */
function mergeRiskDetailPayload(detail, seedRaw = null, seedRow = null) {
  const seed = seedRaw && typeof seedRaw === 'object' ? seedRaw : {};
  const incoming = detail && typeof detail === 'object' ? detail : {};
  const merged = { ...seed, ...incoming };

  const prefer = (key, ...fallbacks) => {
    const chosen = firstNonEmpty(incoming[key], seed[key], ...fallbacks);
    if (chosen) merged[key] = chosen;
  };

  const ip = pickRiskIp(
    incoming.ip,
    incoming.ip_address,
    seed.ip,
    seed.ip_address,
    seedRow?.ip,
    incoming.scan_definition,
    seed.scan_definition,
    seedRow?.scannerType === 'network' || String(incoming.scanner_type || seed.scanner_type || '').toLowerCase() === 'network'
      ? seedRow?.target
      : ''
  );
  if (ip) {
    merged.ip = ip;
  }

  prefer('hostname', seedRow?.hostname);
  prefer('host_os', seed.os);
  prefer('scan_definition', seedRow?.target, ip);
  prefer('port', seedRow?.port);
  prefer('mac_address');
  prefer('mac_vendor');
  prefer('cvss');
  prefer('summary');
  prefer('impact');
  prefer('technical_findings', seed.insight, seed.description);
  prefer('scanner_type', seedRow?.scannerType, seedRow?.source);

  if (merged.cvss_data == null || (typeof merged.cvss_data === 'object' && !Object.keys(merged.cvss_data).length)) {
    if (seed.cvss_data && typeof seed.cvss_data === 'object' && Object.keys(seed.cvss_data).length) {
      merged.cvss_data = seed.cvss_data;
    }
  }

  return merged;
}

function formatRiskScannerLabel(scannerType, deploymentHint = '') {
  const raw = String(scannerType || '').trim().toLowerCase();
  const isNetwork = raw === 'network' || raw.startsWith('net') || raw.includes('openvas');
  if (!isNetwork) {
    if (raw === 'web' || raw.includes('zap')) return 'Web Scan';
    return firstNonEmpty(scannerType, '—');
  }
  const deployment = String(deploymentHint || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');
  if (
    deployment === 'perimeter_scanner'
    || deployment === 'perimeter'
    || deployment.startsWith('perimeter')
  ) {
    return 'Perimeter';
  }
  return 'On-premise';
}

function formatMacAddress(macAddress, macVendor) {
  const mac = firstNonEmpty(macAddress);
  if (!mac) return 'Not found';
  const vendor = firstNonEmpty(macVendor);
  return vendor ? `${mac} - ${vendor}` : mac;
}

const CVSS_V2_METRIC_META = {
  AV: {
    metric: 'Access Vector',
    values: { N: 'Network', A: 'Adjacent Network', L: 'Local' },
  },
  AC: {
    metric: 'Access Complexity',
    values: { H: 'High', M: 'Medium', L: 'Low' },
  },
  Au: {
    metric: 'Authentication',
    values: { M: 'Multiple', S: 'Single', N: 'None' },
  },
  C: {
    metric: 'Confidentiality Impact',
    values: { N: 'None', P: 'Partial', C: 'Complete' },
  },
  I: {
    metric: 'Integrity Impact',
    values: { N: 'None', P: 'Partial', C: 'Complete' },
  },
  A: {
    metric: 'Availability Impact',
    values: { N: 'None', P: 'Partial', C: 'Complete' },
  },
};

const CVSS_V3_METRIC_META = {
  AV: {
    metric: 'Attack Vector',
    values: { N: 'Network', A: 'Adjacent', L: 'Local', P: 'Physical' },
  },
  AC: {
    metric: 'Attack Complexity',
    values: { L: 'Low', H: 'High' },
  },
  PR: {
    metric: 'Privileges Required',
    values: { N: 'None', L: 'Low', H: 'High' },
  },
  UI: {
    metric: 'User Interaction',
    values: { N: 'None', R: 'Required' },
  },
  S: {
    metric: 'Scope',
    values: { U: 'Unchanged', C: 'Changed' },
  },
  C: {
    metric: 'Confidentiality',
    values: { N: 'None', L: 'Low', H: 'High' },
  },
  I: {
    metric: 'Integrity',
    values: { N: 'None', L: 'Low', H: 'High' },
  },
  A: {
    metric: 'Availability',
    values: { N: 'None', L: 'Low', H: 'High' },
  },
};

function normalizeCvssVector(raw) {
  const text = String(raw || '').trim();
  if (!text) return '';
  const match = text.match(/CVSS:\d+\.\d+\/[A-Za-z0-9:/]+|AV:[A-Za-z]\/[A-Za-z0-9:/]+/);
  return match ? match[0] : text;
}

function parseCvssVectorToData(rawVector) {
  const vector = normalizeCvssVector(rawVector);
  if (!vector) return {};
  const isV3 = /^CVSS:3/i.test(vector);
  const meta = isV3 ? CVSS_V3_METRIC_META : CVSS_V2_METRIC_META;
  const parts = vector.replace(/^CVSS:\d+\.\d+\//i, '').split('/');
  const data = {};
  parts.forEach((part) => {
    const sep = part.indexOf(':');
    if (sep <= 0) return;
    const rawKey = part.slice(0, sep);
    const code = part.slice(sep + 1).toUpperCase();
    const resolvedKey =
      (isV3 ? rawKey.toUpperCase() : rawKey.toUpperCase() === 'AU' ? 'Au' : rawKey.toUpperCase());
    const finalMeta = meta[resolvedKey] || meta[rawKey] || null;
    if (!finalMeta) return;
    data[resolvedKey] = {
      metric: finalMeta.metric,
      value: finalMeta.values[code] || `${code}`,
    };
  });
  return data;
}

function stripCvssShortForm(text) {
  return String(text || '').replace(/\s*\([A-Za-z0-9]+\)\s*$/g, '').trim();
}

function cvssMetricEntries(cvssData) {
  if (!cvssData || typeof cvssData !== 'object' || Array.isArray(cvssData)) return [];
  // Omit Access Vector / Attack Vector — redundant with Scanner / target context.
  const order = ['AC', 'Au', 'PR', 'UI', 'S', 'C', 'I', 'A'];
  const keys = [
    ...order.filter((key) => cvssData[key]),
    ...Object.keys(cvssData).filter((key) => !order.includes(key) && key !== 'AV'),
  ];
  return keys
    .map((key) => {
      const item = cvssData[key];
      if (!item || typeof item !== 'object') return null;
      const metric = stripCvssShortForm(firstNonEmpty(item.metric, key));
      const value = stripCvssShortForm(firstNonEmpty(item.value));
      if (!metric || !value) return null;
      return {
        key,
        label: metric,
        value,
        tooltip: firstNonEmpty(item.tooltip),
      };
    })
    .filter(Boolean);
}

function extractCveIds(...candidates) {
  const found = [];
  const seen = new Set();
  const pushFrom = (value) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(pushFrom);
      return;
    }
    if (typeof value === 'object') {
      pushFrom(value.text);
      pushFrom(value.title);
      pushFrom(value.url);
      pushFrom(value.href);
      pushFrom(value.cve);
      return;
    }
    const matches = String(value).match(/CVE-\d{4}-\d+/gi) || [];
    matches.forEach((match) => {
      const id = match.toUpperCase();
      if (seen.has(id)) return;
      seen.add(id);
      found.push(id);
    });
  };
  candidates.forEach(pushFrom);
  return found;
}

async function fetchCvssVectorFromNvd(cveId) {
  const id = String(cveId || '').trim().toUpperCase();
  if (!/^CVE-\d{4}-\d+$/.test(id)) return '';
  try {
    const response = await fetch(
      `https://services.nvd.nist.gov/rest/json/cves/2.0?cveId=${encodeURIComponent(id)}`
    );
    if (!response.ok) return '';
    const body = await response.json();
    const metrics = body?.vulnerabilities?.[0]?.cve?.metrics || {};
    return (
      firstNonEmpty(
        metrics?.cvssMetricV31?.[0]?.cvssData?.vectorString,
        metrics?.cvssMetricV30?.[0]?.cvssData?.vectorString,
        metrics?.cvssMetricV2?.[0]?.cvssData?.vectorString
      ) || ''
    );
  } catch (_) {
    return '';
  }
}

async function enrichRiskDetailCvss(detail) {
  if (!detail || typeof detail !== 'object') return detail;
  const existingVector = normalizeCvssVector(detail.cvss);
  let vector = existingVector;
  let cvssData =
    detail.cvss_data && typeof detail.cvss_data === 'object' && !Array.isArray(detail.cvss_data)
      ? detail.cvss_data
      : {};

  if (!Object.keys(cvssData).length && vector) {
    cvssData = parseCvssVectorToData(vector);
  }

  if (!vector || !Object.keys(cvssData).length) {
    const cves = extractCveIds(
      detail.references,
      detail.reference,
      detail.title,
      detail.summary,
      detail.description
    );
    for (const cveId of cves) {
      const nvdVector = await fetchCvssVectorFromNvd(cveId);
      if (!nvdVector) continue;
      vector = nvdVector;
      cvssData = parseCvssVectorToData(nvdVector);
      if (Object.keys(cvssData).length) break;
    }
  }

  if (!vector && !Object.keys(cvssData).length) return detail;
  return {
    ...detail,
    cvss: vector || detail.cvss || null,
    cvss_data: Object.keys(cvssData).length ? cvssData : detail.cvss_data || {},
  };
}

function cveUrlFromText(text) {
  const match = String(text || '').trim().match(/^CVE-\d{4}-\d+$/i);
  if (!match) return '';
  return `https://nvd.nist.gov/vuln/detail/${match[0].toUpperCase()}`;
}

function parseRiskReferences(raw) {
  const items = [];
  const pushItem = (entry) => {
    if (entry == null || entry === '') return;
    if (typeof entry === 'string') {
      const text = entry.trim();
      if (!text || /^n\/?a$/i.test(text)) return;
      const url = /^(https?:)?\/\//i.test(text) ? text : cveUrlFromText(text);
      items.push({ text, url: url || '' });
      return;
    }
    if (typeof entry === 'object') {
      const text = String(entry.text || entry.title || entry.url || entry.href || '').trim();
      const url = String(entry.url || entry.href || '').trim() || cveUrlFromText(text);
      if (!text && !url) return;
      if (/^n\/?a$/i.test(text)) return;
      items.push({ text: text || url, url });
    }
  };

  const ingest = (value) => {
    if (value == null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach(pushItem);
      return;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return;
      try {
        const parsed = JSON.parse(trimmed);
        ingest(parsed);
        return;
      } catch (_) {
        trimmed.split(/[\n,]+/).forEach((part) => pushItem(part.trim()));
      }
      return;
    }
    if (typeof value === 'object') {
      pushItem(value);
    }
  };

  ingest(raw);
  const seen = new Set();
  return items.filter((item) => {
    const key = `${item.text}|${item.url}`.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getCvssSeverityBand(scoreValue) {
  const numericScore = Number.parseFloat(scoreValue);
  if (!Number.isFinite(numericScore)) return 'unknown';
  if (numericScore >= 9.0) return 'critical';
  if (numericScore >= 7.0) return 'high';
  if (numericScore >= 4.0) return 'medium';
  if (numericScore >= 0.0) return 'low';
  return 'unknown';
}

function parseRiskNotesFromRaw(noteField, currentUser = null) {
  if (noteField == null) return [];
  const raw = typeof noteField === 'string' ? noteField.trim() : JSON.stringify(noteField);
  if (!raw) return [];
  const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const currentUserId = String(currentUser?.uu_id || currentUser?.user_id || '').trim().toLowerCase();
  const currentUserName = String(
    currentUser?.name || currentUser?.display_name || ''
  ).trim();
  const currentUserEmail = String(currentUser?.email || '').trim();

  const resolveNoteUser = (entry) => {
    const authorId = String(entry?.user_uu_id || entry?.user_id || '').trim().toLowerCase();
    if (currentUserId && authorId && authorId === currentUserId) {
      if (currentUserName && !uuidRe.test(currentUserName)) return currentUserName;
      if (currentUserEmail.includes('@')) {
        const local = currentUserEmail.split('@')[0].trim();
        if (local) return local;
      }
    }

    const roleLabels = new Set([
      'admin',
      'administrator',
      'analyst',
      'viewer',
      'partner',
      'superuser',
      'root',
      '1',
      '2',
      '3',
      '4',
    ]);
    const candidates = [entry?.user_name, entry?.user, entry?.display_name, entry?.email];
    for (const candidate of candidates) {
      const value = String(candidate || '').trim();
      if (!value || uuidRe.test(value) || roleLabels.has(value.toLowerCase())) continue;
      if (value.includes('@')) {
        const local = value.split('@')[0].trim();
        if (local) return local;
      }
      return value;
    }
    if (currentUserName && !uuidRe.test(currentUserName) && (!authorId || authorId === currentUserId)) {
      return currentUserName;
    }
    return 'User';
  };

  try {
    const parsed = typeof noteField === 'string' ? JSON.parse(raw) : noteField;
    if (Array.isArray(parsed)) {
      return parsed
        .map((entry, index) => ({
          id: String(entry?.note_id || entry?.id || `note-${index}`),
          user: resolveNoteUser(entry),
          userId: String(entry?.user_uu_id || entry?.user_id || '').trim(),
          createdAt: entry?.date_time_created || entry?.createdAt || entry?.date_time || new Date().toISOString(),
          text: String(entry?.note || entry?.text || '').trim(),
        }))
        .filter((entry) => entry.text);
    }
  } catch (_) {
    /* legacy plain-text note */
  }

  return [
    {
      id: 'legacy-note',
      user: 'System',
      userId: '',
      createdAt: new Date().toISOString(),
      text: raw,
    },
  ];
}

function loadLocalRiskNotes(riskId) {
  try {
    const raw = window.localStorage.getItem(RISK_NOTES_OVERRIDES_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const notes = parsed?.[riskId];
    return Array.isArray(notes) ? notes : [];
  } catch (_) {
    return [];
  }
}

function persistLocalRiskNotes(riskId, notes) {
  try {
    const raw = window.localStorage.getItem(RISK_NOTES_OVERRIDES_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const next = parsed && typeof parsed === 'object' ? parsed : {};
    next[riskId] = notes;
    window.localStorage.setItem(RISK_NOTES_OVERRIDES_KEY, JSON.stringify(next));
  } catch (_) {
    /* ignore storage errors */
  }
}

function readLocalStatusOverride(riskId) {
  try {
    const raw = window.localStorage.getItem(RISK_STATUS_OVERRIDES_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed?.[riskId] || null;
  } catch (_) {
    return null;
  }
}

function persistLocalStatusOverride(riskId, normalized) {
  try {
    const raw = window.localStorage.getItem(RISK_STATUS_OVERRIDES_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const next = parsed && typeof parsed === 'object' ? parsed : {};
    next[riskId] = normalized;
    window.localStorage.setItem(RISK_STATUS_OVERRIDES_KEY, JSON.stringify(next));
  } catch (_) {
    /* ignore storage errors */
  }
}

export default function RiskDetailPageContent({
  riskId,
  scannerHint = '',
  seedRow = null,
  onBack,
  userProfile = null,
}) {
  const detailRequestRef = useRef(0);
  const [activeRiskRow, setActiveRiskRow] = useState(seedRow);
  const [riskDetailLoading, setRiskDetailLoading] = useState(false);
  const [riskDetailError, setRiskDetailError] = useState('');
  const [riskDetailData, setRiskDetailData] = useState(seedRow?.raw || null);
  const [riskNotes, setRiskNotes] = useState([]);
  const [riskNotesHistoryOpen, setRiskNotesHistoryOpen] = useState(true);
  const [riskDetailStatusValue, setRiskDetailStatusValue] = useState('open');
  const [pendingRiskStatusChange, setPendingRiskStatusChange] = useState(null);
  const [noteComposerOpen, setNoteComposerOpen] = useState(false);
  const [noteComposerValue, setNoteComposerValue] = useState('');
  const [noteComposerRequired, setNoteComposerRequired] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSaveError, setNoteSaveError] = useState('');

  const activeRiskRaw = riskDetailData || activeRiskRow?.raw || {};
  const activeRiskCvssScoreRaw =
    activeRiskRaw?.cvss_score ?? activeRiskRaw?.severity_float ?? activeRiskRow?.cvssScore ?? null;
  const activeRiskCvssVector = normalizeCvssVector(activeRiskRaw?.cvss);
  const activeRiskCvssNumeric = Number.parseFloat(activeRiskCvssScoreRaw);
  const activeRiskCvssDisplay = Number.isFinite(activeRiskCvssNumeric)
    ? activeRiskCvssNumeric.toFixed(1)
    : (activeRiskCvssScoreRaw ?? '—');
  const activeRiskCvssBand = getCvssSeverityBand(activeRiskCvssScoreRaw);
  const activeRiskCvssBaseScoreDisplay = activeRiskCvssDisplay;
  const activeRiskCvssMetrics = cvssMetricEntries(
    activeRiskRaw?.cvss_data && Object.keys(activeRiskRaw.cvss_data || {}).length
      ? activeRiskRaw.cvss_data
      : parseCvssVectorToData(activeRiskCvssVector)
  );
  const activeRiskScannerLabel = formatRiskScannerLabel(
    activeRiskRaw?.scanner_type || activeRiskRow?.scannerType || activeRiskRow?.source,
    activeRiskRaw?.scanner_deployment
      || activeRiskRow?.scannerDeployment
      || activeRiskRow?.raw?.scanner_deployment
  );
  const activeRiskHostname = firstNonEmpty(
    activeRiskRaw?.hostname,
    activeRiskRaw?.host,
    activeRiskRow?.hostname
  );
  const activeRiskIp = pickRiskIp(
    activeRiskRaw?.ip,
    activeRiskRaw?.ip_address,
    activeRiskRow?.ip,
    activeRiskRaw?.scan_definition,
    activeRiskRow?.scannerType === 'network' ? activeRiskRow?.target : ''
  );
  const activeRiskScanDefinition = firstNonEmpty(
    activeRiskRaw?.scan_definition,
    activeRiskRaw?.target,
    activeRiskRow?.target,
    activeRiskIp
  );
  const activeRiskReferences = parseRiskReferences(
    activeRiskRaw?.references ?? activeRiskRaw?.reference
  );

  useEffect(() => {
    const closeDetail = () => {
      if (typeof onBack === 'function') onBack();
    };
    window.addEventListener('xspectre:tour-close-risk-detail', closeDetail);
    return () => {
      window.removeEventListener('xspectre:tour-close-risk-detail', closeDetail);
    };
  }, [onBack]);

  const applyRiskStatusChange = useCallback((normalized, detailRaw = null) => {
    const rowId = String(riskId || activeRiskRow?.id || '').trim();
    if (!normalized || !rowId) return;

    persistLocalStatusOverride(rowId, normalized);

    setActiveRiskRow((prev) =>
      prev
        ? {
            ...prev,
            status: normalized,
            statusLabel: riskStatusLabelFromValue(normalized),
          }
        : prev
    );
    setRiskDetailData((prev) => {
      const merged = mergeRiskDetailPayload(
        detailRaw && typeof detailRaw === 'object' ? detailRaw : null,
        prev,
        activeRiskRow
      );
      if (!merged || !Object.keys(merged).length) return prev;
      return {
        ...merged,
        accepted_vuln: normalized === 'accepted' ? 'Yes' : 'No',
        false_positive: normalized === 'false_positive' ? 'Yes' : 'No',
        vuln_status: normalized === 'open' ? 'Open' : 'Closed',
      };
    });
    setRiskDetailStatusValue(normalized);
  }, [activeRiskRow, riskId]);

  useEffect(() => {
    const id = String(riskId || '').trim();
    if (!id) return;

    const rowSeed = seedRow && String(seedRow.id || '').trim() === id ? seedRow : null;
    const scanner = String(scannerHint || rowSeed?.scannerType || rowSeed?.raw?.scanner_type || '').trim() || undefined;

    setActiveRiskRow(rowSeed);
    setRiskDetailData(rowSeed?.raw || null);
    setRiskDetailError('');
    setNoteComposerOpen(false);
    setNoteComposerValue('');
    setNoteComposerRequired(false);
    setPendingRiskStatusChange(null);
    setNoteSaveError('');
    setRiskDetailStatusValue(resolveRiskDetailStatusValue(rowSeed?.raw, rowSeed));

    if (!UUID_RE.test(id)) {
      setRiskNotes(loadLocalRiskNotes(id));
      const localStatus = readLocalStatusOverride(id);
      if (localStatus) {
        setRiskDetailStatusValue(String(localStatus).trim().toLowerCase());
      }
      setRiskDetailLoading(false);
      return;
    }

    setRiskNotes([]);
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    setRiskDetailLoading(true);

    const loadDetail = async () => {
      const hinted = String(scanner || '').trim().toLowerCase();
      const attempts = [];
      if (hinted) attempts.push(hinted);
      // Wrong scanner hints (common from asset inventory) must not block the real record.
      if (hinted !== 'network') attempts.push('network');
      if (hinted !== 'web') attempts.push('web');
      attempts.push('');

      let lastError = null;
      for (const attempt of attempts) {
        try {
          const query = attempt ? { scanner: attempt } : {};
          const detail = await getDashboardVulnerabilityDetail(id, query);
          if (detailRequestRef.current !== requestId) return;
          const merged = mergeRiskDetailPayload(detail, rowSeed?.raw, rowSeed);
          const enriched = await enrichRiskDetailCvss(merged);
          if (detailRequestRef.current !== requestId) return;
          setRiskDetailData(enriched);
          setRiskNotes(parseRiskNotesFromRaw(enriched?.note ?? detail?.note, userProfile));
          setRiskDetailStatusValue(resolveRiskDetailStatusValue(enriched, rowSeed));
          setRiskDetailError('');
          return;
        } catch (err) {
          lastError = err;
        }
      }
      if (detailRequestRef.current !== requestId) return;
      setRiskDetailError(lastError?.message || 'Unable to load vulnerability detail');
    };

    loadDetail().finally(() => {
      if (detailRequestRef.current === requestId) {
        setRiskDetailLoading(false);
      }
    });
  }, [riskId, scannerHint, seedRow?.id, userProfile]);

  const openNoteComposer = useCallback((required = false, pendingStatus = null) => {
    setPendingRiskStatusChange(pendingStatus);
    setNoteComposerRequired(required);
    setNoteComposerValue('');
    setNoteSaveError('');
    setNoteComposerOpen(true);
  }, []);

  const handleRiskDetailStatusChange = useCallback((nextStatus) => {
    const normalized = String(nextStatus || '').trim().toLowerCase();
    if (!normalized || normalized === riskDetailStatusValue) return;
    openNoteComposer(true, normalized);
  }, [openNoteComposer, riskDetailStatusValue]);

  const saveNoteComposer = useCallback(async () => {
    const noteText = String(noteComposerValue || '').trim();
    if (!noteText || noteSaving) return;

    const pendingStatus = pendingRiskStatusChange;
    if (noteComposerRequired && !pendingStatus) return;

    const rowId = String(riskId || activeRiskRow?.id || '').trim();
    if (!rowId) return;

    const createdAt = new Date().toISOString();
    const statusLabel = pendingStatus ? riskStatusLabelFromValue(pendingStatus) : '';
    const noteBody = pendingStatus
      ? `Status changed to ${statusLabel}: ${noteText}`
      : noteText;
    const scanner = String(
      scannerHint || activeRiskRaw?.scanner_type || activeRiskRow?.scannerType || ''
    ).trim() || undefined;

    setNoteSaving(true);
    setNoteSaveError('');

    try {
      if (UUID_RE.test(rowId)) {
        const noteResponse = await postDashboardVulnerabilityNote(rowId, { note: noteBody, scanner });
        let nextDetail = null;

        if (pendingStatus) {
          await patchDashboardVulnerabilityStatus(rowId, { status: pendingStatus, scanner });
          nextDetail = await getDashboardVulnerabilityDetail(rowId, { scanner });
          applyRiskStatusChange(pendingStatus, nextDetail);
        } else {
          nextDetail = await getDashboardVulnerabilityDetail(rowId, { scanner });
        }

        const mergedDetail = mergeRiskDetailPayload(nextDetail, activeRiskRaw, activeRiskRow);
        const hydratedNotes = parseRiskNotesFromRaw(mergedDetail?.note || noteResponse?.note, userProfile);
        setRiskDetailData(mergedDetail);
        setRiskNotes(hydratedNotes);
      } else {
        const authorName =
          String(userProfile?.name || userProfile?.display_name || '').trim() ||
          (String(userProfile?.email || '').includes('@')
            ? String(userProfile.email).split('@')[0].trim()
            : '') ||
          'You';
        const nextNotes = [
          { id: `note-${createdAt}`, user: authorName, createdAt, text: noteBody },
          ...loadLocalRiskNotes(rowId),
        ];
        setRiskNotes(nextNotes);
        persistLocalRiskNotes(rowId, nextNotes);
        setRiskDetailData((prev) => (prev ? { ...prev, note: noteBody } : prev));
        if (pendingStatus) {
          applyRiskStatusChange(pendingStatus);
        }
      }

      setNoteComposerOpen(false);
      setNoteComposerValue('');
      setNoteComposerRequired(false);
      setPendingRiskStatusChange(null);
    } catch (err) {
      setNoteSaveError(err?.message || 'Unable to save note');
    } finally {
      setNoteSaving(false);
    }
  }, [
    activeRiskRaw?.scanner_type,
    activeRiskRow?.id,
    activeRiskRow?.scannerType,
    applyRiskStatusChange,
    noteComposerRequired,
    noteComposerValue,
    noteSaving,
    pendingRiskStatusChange,
    riskId,
    scannerHint,
    userProfile,
  ]);

  const title = activeRiskRow?.title || activeRiskRaw?.title || 'Vulnerability Detail';

  const severityLabel = firstNonEmpty(
    activeRiskRow?.severity,
    activeRiskRaw?.severity,
    'Unknown'
  );
  const severityKey = String(
    activeRiskRow?.severityKey || severityLabel || 'unknown'
  )
    .trim()
    .toLowerCase();

  return (
    <div className="risk-detail-page-stack" data-tour="risk-detail-page">
      <div className="risk-detail-page-top">
        <div className="risk-detail-page-heading" data-tour="risk-detail-header">
          <div className="risk-detail-title-wrap">
            <div className="risk-detail-title-badges">
              <span className={`risk-detail-severity-badge risk-detail-severity-badge--${severityKey || 'unknown'}`}>
                {severityLabel}
              </span>
              <span className={`risk-detail-chip risk-detail-chip--cvss risk-detail-chip--cvss-${activeRiskCvssBand}`}>
                CVSS {activeRiskCvssDisplay}
              </span>
            </div>
            <h2 className="assets-detail-modal-title risk-detail-modal-title">{title}</h2>
          </div>
          <div className="assets-detail-modal-actions risk-detail-modal-actions">
            <div className="risk-detail-header-controls risk-detail-header-controls--top" data-tour="risk-detail-actions">
              <RiskDetailStatusSelect
                value={riskDetailStatusValue}
                disabled={noteSaving}
                onChange={handleRiskDetailStatusChange}
              />
              <button
                type="button"
                className="risk-detail-notes-btn risk-detail-notes-btn--header"
                data-tour="risk-detail-add-note"
                onClick={() => openNoteComposer(false, null)}
                disabled={noteSaving}
              >
                + Add note
              </button>
            </div>
          </div>
        </div>

        <div className="risk-detail-page-toolbar">
          <button
            type="button"
            className="risk-detail-back-btn"
            data-tour="risk-detail-back"
            onClick={onBack}
          >
            ← back to all vulnerabilities
          </button>
        </div>
      </div>

      <div className="risks-page risk-detail-page">

        {riskDetailLoading ? (
          <div className="assets-detail-loading" role="status" aria-live="polite">
            Loading vulnerability details…
          </div>
        ) : null}
        {!riskDetailLoading && riskDetailError ? (
          <div className="assets-detail-error" role="alert">
            {riskDetailError}
          </div>
        ) : null}

        <div className="risk-detail-grid">
          <section
            className="risk-detail-card risk-detail-card--full risk-detail-notes-editor"
            data-tour="risk-detail-notes"
          >
            <h3 className="risk-detail-notes-heading">NOTES</h3>

            {noteComposerOpen ? (
              <div className="risk-detail-note-composer">
                {noteComposerRequired ? (
                  <div className="profile-settings-message profile-settings-error" role="alert">
                    A note is required when changing risk status
                    {pendingRiskStatusChange
                      ? ` to ${riskStatusLabelFromValue(pendingRiskStatusChange)}`
                      : ''}
                    .
                  </div>
                ) : null}
                {noteSaveError ? (
                  <div className="profile-settings-message profile-settings-error" role="alert">
                    {noteSaveError}
                  </div>
                ) : null}
                <textarea
                  className="risk-detail-notes-input"
                  value={noteComposerValue}
                  onChange={(event) => setNoteComposerValue(event.target.value)}
                  placeholder={noteComposerRequired ? 'Explain why the status is changing…' : 'Type note...'}
                  rows={5}
                  autoFocus
                  disabled={noteSaving}
                />
                <div className="risk-detail-note-composer-actions">
                  <button
                    type="button"
                    className="risk-detail-notes-btn risk-note-save-btn"
                    onClick={saveNoteComposer}
                    disabled={!String(noteComposerValue || '').trim() || noteSaving}
                  >
                    Save
                  </button>
                </div>
              </div>
            ) : null}

            {riskNotes.length ? (
              (() => {
                const [latestNote, ...historyNotes] = riskNotes;
                return (
                  <div className="risk-detail-notes-list">
                    <div className="risk-detail-note-item risk-detail-note-item--latest">
                      <div className="risk-detail-note-header">
                        <span className="risk-detail-note-user">{latestNote.user || 'Unknown'}</span>
                        <span className="risk-detail-note-date">{formatRiskDetectedAt(latestNote.createdAt)}</span>
                      </div>
                      <div className="risk-detail-note-text">{latestNote.text}</div>
                    </div>

                    {historyNotes.length > 0 && (
                      <div className="risk-detail-notes-history">
                        <button
                          type="button"
                          className="risk-detail-notes-history-toggle"
                          onClick={() => setRiskNotesHistoryOpen((prev) => !prev)}
                          aria-expanded={riskNotesHistoryOpen}
                        >
                          <span>{historyNotes.length} earlier {historyNotes.length === 1 ? 'note' : 'notes'}</span>
                          <span className={`risk-detail-notes-history-chevron${riskNotesHistoryOpen ? ' is-open' : ''}`} aria-hidden="true">⌃</span>
                        </button>
                        {riskNotesHistoryOpen && (
                          <div className="risk-detail-notes-history-list">
                            {historyNotes.map((note) => (
                              <div key={note.id} className="risk-detail-note-item">
                                <div className="risk-detail-note-header">
                                  <span className="risk-detail-note-user">{note.user || 'Unknown'}</span>
                                  <span className="risk-detail-note-date">{formatRiskDetectedAt(note.createdAt)}</span>
                                </div>
                                <div className="risk-detail-note-text">{note.text}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              !noteComposerOpen && (
                <div className="risk-detail-note-empty">No notes yet. Use Add note to create one.</div>
              )
            )}
          </section>

          <section className="risk-detail-card" data-tour="risk-detail-instance">
            <h3 className="risk-detail-card-title">Instance</h3>
            <table className="assets-detail-table risk-detail-kv-table">
              <tbody>
                <tr><th scope="row">Hostname</th><td>{activeRiskHostname || 'NA'}</td></tr>
                <tr><th scope="row">IP Address</th><td>{activeRiskIp || '—'}</td></tr>
                <tr>
                  <th scope="row">MAC Address</th>
                  <td>{formatMacAddress(activeRiskRaw?.mac_address, activeRiskRaw?.mac_vendor)}</td>
                </tr>
                <tr>
                  <th scope="row">Operating System</th>
                  <td>{firstNonEmpty(activeRiskRaw?.host_os, activeRiskRaw?.os) || 'Unknown'}</td>
                </tr>
                <tr><th scope="row">Scanner</th><td>{activeRiskScannerLabel}</td></tr>
                <tr><th scope="row">Scan Definition</th><td>{activeRiskScanDefinition || '—'}</td></tr>
                <tr><th scope="row">Port</th><td>{firstNonEmpty(activeRiskRaw?.port, activeRiskRow?.port) || '—'}</td></tr>
                <tr><th scope="row">CVSS Base Score</th><td>{activeRiskCvssBaseScoreDisplay || '—'}</td></tr>
                {activeRiskCvssMetrics.map((metric) => (
                  <tr key={`cvss-metric-${metric.key}`}>
                    <th scope="row" title={metric.tooltip || undefined}>{metric.label}</th>
                    <td>{metric.value}</td>
                  </tr>
                ))}
                <tr><th scope="row">Detected At</th><td>{formatRiskDetectedAt(activeRiskRaw?.date_time || activeRiskRow?.detectedAt)}</td></tr>
              </tbody>
            </table>
          </section>

          <section className="risk-detail-card" data-tour="risk-detail-description">
            <h3 className="risk-detail-card-title">Description</h3>
            <div className="risk-detail-block">
              <div className="risk-detail-subtitle">Summary</div>
              <p>{activeRiskRaw?.summary || activeRiskRaw?.description || activeRiskRow?.title || 'No summary available.'}</p>
            </div>
            <div className="risk-detail-block">
              <div className="risk-detail-subtitle">Impact</div>
              <p>{activeRiskRaw?.impact || 'Impact details are not available for this risk yet.'}</p>
            </div>
          </section>

          <section className="risk-detail-card" data-tour="risk-detail-technical">
            <h3 className="risk-detail-card-title">Technical Findings</h3>
            <p className="risk-detail-mono">
              {activeRiskRaw?.technical_findings ||
                (Array.isArray(activeRiskRaw?.instance_details) ? activeRiskRaw.instance_details.join('\n') : '') ||
                activeRiskRaw?.evidence ||
                activeRiskRaw?.plugin_output ||
                'Technical findings are not available for this risk yet.'}
            </p>
          </section>

          <section className="risk-detail-card" data-tour="risk-detail-solution">
            <h3 className="risk-detail-card-title">Solution</h3>
            <div className="risk-detail-block">
              <div className="risk-detail-subtitle">Solution Type</div>
              <p>{activeRiskRaw?.solution_type || 'Vendor Fix'}</p>
            </div>
            <div className="risk-detail-block">
              <div className="risk-detail-subtitle">Solution Action</div>
              <p>{activeRiskRaw?.solution || activeRiskRaw?.remediation || 'No remediation guidance is currently available.'}</p>
            </div>
          </section>

          <section className="risk-detail-card risk-detail-card--full" data-tour="risk-detail-details">
            <h3 className="risk-detail-card-title">Vulnerability Details</h3>
            <p>
              {activeRiskRaw?.description ||
                activeRiskRaw?.summary ||
                activeRiskRaw?.technical_findings ||
                'No additional vulnerability details are available for this risk yet.'}
            </p>
          </section>

          <section
            className="risk-detail-card risk-detail-card--full"
            aria-label="References"
            data-tour="risk-detail-references"
          >
            <h3 className="risk-detail-card-title">References</h3>
            {activeRiskReferences.length ? (
              <ul className="risk-detail-references">
                {activeRiskReferences.map((ref, index) => (
                  <li key={`${ref.text}-${index}`}>
                    {ref.url ? (
                      <a href={ref.url} target="_blank" rel="noopener noreferrer">
                        {ref.text}
                      </a>
                    ) : (
                      <span>{ref.text}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No references are available for this risk yet.</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
